<template>
  <transition name="modal-fade">
    <div v-if="visible" class="ucm-overlay" @mousedown.self="$emit('close')">
      <div class="ucm-card">
        <!-- Close -->
        <button class="ucm-close" @click="$emit('close')">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M1 1l16 16M17 1L1 17" stroke="#555" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>

        <!-- Title -->
        <h2 class="ucm-title">Upload Contract</h2>
        <p class="ucm-subtitle">Must be in PDF format</p>

        <!-- Drop Zone -->
        <div
          class="ucm-dropzone"
          :class="{ 'ucm-dropzone--hover': isDragOver, 'ucm-dropzone--selected': selectedFile }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="onDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept="application/pdf"
            style="display:none"
            @change="onFileSelect"
          />

          <template v-if="!selectedFile">
            <div class="ucm-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M20 28V12M20 28l-6-6M20 28l6-6" stroke="#aaa" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 32h24" stroke="#aaa" stroke-width="2.2" stroke-linecap="round"/>
              </svg>
            </div>
            <p class="ucm-drop-label">Click to upload or drag and drop</p>
            <p class="ucm-drop-hint">PDF up to 10MB</p>
          </template>

          <template v-else>
            <div class="ucm-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M10 20l8 8 12-14" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <p class="ucm-drop-label ucm-drop-label--selected">{{ selectedFile.name }}</p>
            <p class="ucm-drop-hint">{{ fileSize }} · Click to change</p>
          </template>
        </div>

        <!-- Actions -->
        <div class="ucm-actions">
          <button class="ucm-btn-cancel" @click="$emit('close')">Cancel</button>
          <button
            class="ucm-btn-continue"
            :disabled="! selectedFile"
            @click="proceed"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'UploadContractModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragOver: false,
      selectedFile: null,
    };
  },
  computed: {
    fileSize() {
      if (!this.selectedFile) return '';
      const kb = this.selectedFile.size / 1024;
      return kb >= 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb.toFixed(0) + ' KB';
    },
  },
  watch: {
    visible(v) {
      if (!v) this.selectedFile = null;
    },
  },
  methods: {
    onDrop(e) {
      this.isDragOver = false;
      const file = e.dataTransfer.files[0];
      if (file) this.validateAndSet(file);
    },
    onFileSelect(e) {
      const file = e.target.files[0];
      if (file) this.validateAndSet(file);
      e.target.value = '';
    },
    validateAndSet(file) {
      if (file.type !== 'application/pdf') {
        this.$message && this.$message.error('Please upload a PDF file.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.$message && this.$message.error('PDF must be under 10MB.');
        return;
      }
      this.selectedFile = file;
    },
    proceed() {
      if (! this.selectedFile) return;
      this.$emit('file-selected', this.selectedFile);
    },
  },
};
</script>

<style scoped>
.ucm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(2px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.ucm-card {
  position: relative;
  background: #fff;
  border-radius: 20px;
  padding: 48px 48px 40px;
  width: 100%;
  max-width: 560px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.14);
  text-align: center;
}

.ucm-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  opacity: 0.6;
  transition: opacity 0.15s;
}
.ucm-close:hover { opacity: 1; }

.ucm-title {
  font-size: 28px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px;
  letter-spacing: -0.3px;
  font-family: Georgia, 'Times New Roman', serif;
}

.ucm-subtitle {
  font-size: 14px;
  color: #888;
  margin: 0 0 28px;
}

.ucm-dropzone {
  border: 1.5px dashed #ddd;
  border-radius: 12px;
  background: #f9f9f9;
  padding: 48px 24px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  margin-bottom: 28px;
}

.ucm-dropzone:hover,
.ucm-dropzone--hover {
  border-color: #aaa;
  background: #f2f2f2;
}

.ucm-dropzone--selected {
  border-color: #22c55e;
  background: #f0fdf4;
}

.ucm-icon {
  margin: 0 auto 14px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ucm-drop-label {
  font-size: 15px;
  color: #333;
  margin: 0 0 6px;
  font-weight: 500;
}

.ucm-drop-label--selected {
  color: #16a34a;
}

.ucm-drop-hint {
  font-size: 12px;
  color: #aaa;
  margin: 0;
}

.ucm-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.ucm-btn-cancel {
  padding: 10px 24px;
  border-radius: 8px;
  border: 1.5px solid #ddd;
  background: #fff;
  color: #555;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.ucm-btn-cancel:hover { background: #f5f5f5; }

.ucm-btn-continue {
  padding: 10px 28px;
  border-radius: 8px;
  border: none;
  background: #1a1a2e;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.ucm-btn-continue:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.ucm-btn-continue:not(:disabled):hover { opacity: 0.85; }

/* Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
